import { useTranslation } from 'react-i18next';
import type { ClinicBenchmarkResult } from '@/application/metrics/professionalClinicBenchmark';
import { formatCurrency, formatInteger } from '@/presentation/components/metrics/metricFormatters';
import { formatSigned } from '@/presentation/utils/metricDeltas';

interface ProfessionalClinicBenchmarkPanelProps {
  benchmark: ClinicBenchmarkResult;
  clinicName: string;
  periodLabel: string;
  locale: string;
}

function deltaToneClass(deltaPct: number | null): string {
  if (deltaPct === null) return 'text-gray-600 dark:text-gray-300';
  if (deltaPct > 0) return 'text-teal-700 dark:text-teal-300';
  if (deltaPct < 0) return 'text-amber-800 dark:text-amber-200';
  return 'text-gray-600 dark:text-gray-300';
}

function MetricLine({
  label,
  selfLabel,
  averageLabel,
  deltaLabel,
  deltaPct,
}: {
  label: string;
  selfLabel: string;
  averageLabel: string;
  deltaLabel: string;
  deltaPct: number | null;
}) {
  return (
    <li className="rounded-md border border-[var(--bi-line)] bg-white/70 px-3 py-2.5 dark:bg-white/[0.04]">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
        <span className="font-medium">{selfLabel}</span>
        <span className="text-gray-400 dark:text-gray-500"> · </span>
        <span>{averageLabel}</span>
        <span className="text-gray-400 dark:text-gray-500"> · </span>
        <span className={`font-semibold tabular-nums ${deltaToneClass(deltaPct)}`}>{deltaLabel}</span>
      </p>
    </li>
  );
}

/**
 * Comparación simple vs promedio de la clínica (ingresos + turnos).
 * Esbozo estable para #37 — sin gráficos.
 */
export function ProfessionalClinicBenchmarkPanel({
  benchmark,
  clinicName,
  periodLabel,
  locale,
}: ProfessionalClinicBenchmarkPanelProps) {
  const { t } = useTranslation();

  const deltaText = (deltaPct: number | null) => {
    if (deltaPct === null) return '—';
    const signed = `${formatSigned(locale, deltaPct)}%`;
    if (deltaPct > 0) {
      return t('domain.professionals.detail.benchmark.above', { delta: signed });
    }
    if (deltaPct < 0) {
      return t('domain.professionals.detail.benchmark.below', { delta: signed });
    }
    return t('domain.professionals.detail.benchmark.even', { delta: signed });
  };

  return (
    <section
      data-testid="professional-clinic-benchmark"
      className="space-y-2 rounded-xl border border-[var(--bi-line)] bg-white/80 p-4 dark:bg-white/5"
      aria-label={t('domain.professionals.detail.benchmark.title')}
    >
      <div>
        <h2 className="text-sm font-semibold text-gray-950 dark:text-white">
          {t('domain.professionals.detail.benchmark.title')}
        </h2>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {t('domain.professionals.detail.benchmark.subtitle', {
            clinic: clinicName,
            period: periodLabel,
          })}
        </p>
      </div>

      {benchmark.noClinicData ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('domain.professionals.detail.benchmark.noData')}
        </p>
      ) : benchmark.insufficientBase ? (
        <p
          data-testid="professional-clinic-benchmark-insufficient"
          className="text-sm text-gray-500 dark:text-gray-400"
        >
          {t('domain.professionals.detail.benchmark.insufficient')}
        </p>
      ) : (
        <ul className="space-y-2">
          {benchmark.ingresos ? (
            <MetricLine
              label={t('domain.professionals.detail.revenue')}
              selfLabel={t('domain.professionals.detail.benchmark.you', {
                value: formatCurrency(benchmark.ingresos.self, locale),
              })}
              averageLabel={t('domain.professionals.detail.benchmark.clinicAvg', {
                value: formatCurrency(benchmark.ingresos.average, locale),
              })}
              deltaLabel={deltaText(benchmark.ingresos.deltaPct)}
              deltaPct={benchmark.ingresos.deltaPct}
            />
          ) : null}
          {benchmark.turnos ? (
            <MetricLine
              label={t('domain.professionals.detail.appointments')}
              selfLabel={t('domain.professionals.detail.benchmark.you', {
                value: formatInteger(benchmark.turnos.self, locale),
              })}
              averageLabel={t('domain.professionals.detail.benchmark.clinicAvg', {
                value: formatInteger(Math.round(benchmark.turnos.average), locale),
              })}
              deltaLabel={deltaText(benchmark.turnos.deltaPct)}
              deltaPct={benchmark.turnos.deltaPct}
            />
          ) : null}
          {!benchmark.ingresos && !benchmark.turnos ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('domain.professionals.detail.benchmark.noData')}
            </p>
          ) : null}
        </ul>
      )}
    </section>
  );
}
